
import { useState, useEffect } from 'react';
import { convertToSignedUrl, extractFilePathFromUrl, getSignedMediaUrl } from '@/utils/signedMediaUtils';
import { imageCacheService } from '@/core/services/ImageCacheService';
import { debugLogger } from '@/utils/debugLogger';

interface UseSignedMediaUrlProps {
  mediaUrl: string | null;
  contentType?: 'image' | 'audio' | 'text';
}

export const useSignedMediaUrl = ({ mediaUrl, contentType }: UseSignedMediaUrlProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSignedUrl = async () => {
      // Detailed logging for debugging
      debugLogger.debug('[useSignedMediaUrl] Starting load:', {
        mediaUrl,
        contentType,
        isFullUrl: mediaUrl?.includes('supabase.co/storage/'),
        isChatMedia: mediaUrl?.includes('/chat_media/'),
        isPublicUrl: mediaUrl?.includes('/object/public/'),
        urlLength: mediaUrl?.length || 0
      });

      if (!mediaUrl || contentType === 'text') {
        setSignedUrl(null);
        return;
      }

      // If it's already a full Supabase storage URL
      if (mediaUrl.includes('supabase.co/storage/')) {
        debugLogger.debug('[useSignedMediaUrl] Full URL detected:', mediaUrl);
        
        // Detect if it's chat_media (private bucket)
        const isChatMedia = mediaUrl.includes('/chat_media/');
        
        // If chat_media, FORCE extraction of path and generation of signed URL
        if (isChatMedia) {
          debugLogger.debug('[useSignedMediaUrl] chat_media detected, extracting path for signed URL');
          const filePath = extractFilePathFromUrl(mediaUrl);
          
          if (filePath) {
            const signedUrlResult = await getSignedMediaUrl(filePath, 24 * 60 * 60);
            if (signedUrlResult && contentType === 'image') {
              try {
                const cachedUrl = await imageCacheService.getImage(signedUrlResult);
                setSignedUrl(cachedUrl);
                setIsLoading(false);
                return;
              } catch (error) {
                debugLogger.error('[useSignedMediaUrl] Image cache error:', error);
                setSignedUrl(signedUrlResult);
                setIsLoading(false);
                return;
              }
            } else if (signedUrlResult) {
              setSignedUrl(signedUrlResult);
              setIsLoading(false);
              return;
            }
          }
          
          setError('Failed to generate signed URL for chat media');
          setIsLoading(false);
          return;
        }
        
        // For other public buckets, use directly
        if (contentType === 'image') {
          try {
            const cachedUrl = await imageCacheService.getImage(mediaUrl);
            setSignedUrl(cachedUrl);
          } catch (error) {
            debugLogger.error('[useSignedMediaUrl] Image cache error:', error);
            setSignedUrl(mediaUrl);
          }
        } else {
          setSignedUrl(mediaUrl);
        }
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // For images, use the image cache service
        if (contentType === 'image') {
          debugLogger.debug('[useSignedMediaUrl] Using image cache for:', mediaUrl);
          
          // Check if it's already a signed URL (contains 'token=')
          if (mediaUrl.includes('token=')) {
            const cachedUrl = await imageCacheService.getImage(mediaUrl);
            setSignedUrl(cachedUrl);
            return;
          }

          // Check if it's a file path (not a full URL)
          let finalSignedUrl: string | null;
          if (mediaUrl.startsWith('conversations/') || mediaUrl.startsWith('images/') || mediaUrl.startsWith('audio/') || mediaUrl.startsWith('verification-documents/')) {
            // It's a file path, get signed URL directly with 24h expiration
            finalSignedUrl = await getSignedMediaUrl(mediaUrl, 24 * 60 * 60); // 24 hours
          } else if (mediaUrl.includes('/chat_media/')) {
            // It's a full URL of chat_media, extract path first
            debugLogger.debug('[useSignedMediaUrl] Extracting path from chat_media URL');
            const filePath = extractFilePathFromUrl(mediaUrl);
            if (filePath) {
              finalSignedUrl = await getSignedMediaUrl(filePath, 24 * 60 * 60);
            } else {
              finalSignedUrl = await convertToSignedUrl(mediaUrl, 24 * 60 * 60);
            }
          } else {
            // Other cases
            finalSignedUrl = await convertToSignedUrl(mediaUrl, 24 * 60 * 60); // 24 hours
          }

          if (finalSignedUrl) {
            // Use image cache service to get a cached blob URL
            const cachedUrl = await imageCacheService.getImage(finalSignedUrl);
            setSignedUrl(cachedUrl);
          } else {
            setError('Failed to generate signed URL');
            setSignedUrl(mediaUrl); // Fallback to original URL
          }
        } else {
          // For non-images (audio), use the original logic but with longer expiration
          debugLogger.debug('[useSignedMediaUrl] Processing non-image media:', mediaUrl);
          
          // Check if it's already a signed URL (contains 'token=')
          if (mediaUrl.includes('token=')) {
            setSignedUrl(mediaUrl);
            return;
          }

          // Check if it's a file path (not a full URL)
          let finalSignedUrl: string | null;
          if (mediaUrl.startsWith('conversations/') || mediaUrl.startsWith('images/') || mediaUrl.startsWith('audio/') || mediaUrl.startsWith('verification-documents/')) {
            // It's a file path, get signed URL directly with 24h expiration
            finalSignedUrl = await getSignedMediaUrl(mediaUrl, 24 * 60 * 60); // 24 hours
          } else if (mediaUrl.includes('/chat_media/')) {
            // It's a full URL of chat_media, extract path first
            debugLogger.debug('[useSignedMediaUrl] Extracting path from chat_media URL for audio');
            const filePath = extractFilePathFromUrl(mediaUrl);
            if (filePath) {
              finalSignedUrl = await getSignedMediaUrl(filePath, 24 * 60 * 60);
            } else {
              finalSignedUrl = await convertToSignedUrl(mediaUrl, 24 * 60 * 60);
            }
          } else {
            // Other cases
            finalSignedUrl = await convertToSignedUrl(mediaUrl, 24 * 60 * 60); // 24 hours
          }

          if (finalSignedUrl) {
            setSignedUrl(finalSignedUrl);
          } else {
            setError('Failed to generate signed URL');
            setSignedUrl(mediaUrl); // Fallback to original URL
          }
        }
      } catch (err) {
        debugLogger.error('[useSignedMediaUrl] Error loading signed URL:', err);
        setError('Error loading media');
        setSignedUrl(mediaUrl); // Fallback to original URL
      } finally {
        setIsLoading(false);
      }
    };

    loadSignedUrl();
  }, [mediaUrl, contentType]);

  return {
    signedUrl,
    isLoading,
    error
  };
};
